import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { apiRequestCall } from 'app/services/api';
import { cancelationSubscribe } from 'app/services/api/utils';
import { ApiResource } from 'app/services/api/enums';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { WalletStoreStatus } from './enum';
import { WalletState } from './interfaces';
import { singleCurrencyInitialState } from './adapter';
import { ExchangeRatePayload } from 'app/interfaces/socket/exchange-rate-payload';
import { AvailableBalance, SystemCurrency } from '@okletsplay/core';
import { getBalance } from '../user-payment-accounts/repository';
import { getExchangeRates } from '../exchange-rates/repository';
import { exchangeRatesReducers } from '../exchange-rates/slice';

export const getSingleCurrencyBalance = createAsyncThunk(
  `${EntityStoreName.Wallet}/getBalance`,
  (properties, { signal }): Promise<AvailableBalance> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<AvailableBalance>({
      params: {
        method: 'GET',
        resource: ApiResource.Payments,
        urlPrefix: 'users/balance',
        cancelToken,
      },
    });
  }
);

export const updateExchangeRateReducer = (
  state: WalletState,
  action: PayloadAction<ExchangeRatePayload>
): void => {
  state.entity.exchangeRate = action.payload ?? state.entity.exchangeRate;
};

export const registerReducers = (builder: ActionReducerMapBuilder<WalletState>): void => {
  builder
    .addCase(getSingleCurrencyBalance.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, WalletStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getSingleCurrencyBalance.fulfilled, (state, action) => {
      Object.assign(state.entity, action.payload);
      entityStoreStatus.removeRequestId(state, WalletStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getSingleCurrencyBalance.rejected, (state, action) => {
      state.entity = singleCurrencyInitialState.entity;
      entityStoreStatus.removeRequestId(state, WalletStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getBalance.fulfilled, (state, action) => {
      // Info: This is a bridge functionality, util gaming activities with primary currency are not implemented. See: https://okletsplay.atlassian.net/browse/OK-1941.
      //   Tag: [wallet-bridge]
      if (!Array.isArray(action.payload)) return;

      const balanceEntity = action.payload.find(
        (balance) => balance.currency === SystemCurrency.OKLP
      );
      state.entity.balance = balanceEntity?.balance ?? state.entity.balance;
      state.entity.depositAddress = balanceEntity?.depositAddress ?? state.entity.depositAddress;
    })
    .addCase(getExchangeRates.fulfilled, (state, action) => {
      // Tag: [wallet-bridge]
      if (!Array.isArray(action.payload)) return;

      const exchangeRate = action.payload.find(
        (rate) => rate.to === SystemCurrency.OKLP && rate.from === SystemCurrency.USDT
      );
      state.entity.exchangeRate = exchangeRate?.rate ?? state.entity.exchangeRate;
    })
    .addCase(exchangeRatesReducers.updateExchangeRates, (state, action) => {
      // Tag: [wallet-bridge]
      if (!Array.isArray(action.payload)) return;

      const exchangeRate = action.payload.find(
        (rate) => rate.to === SystemCurrency.OKLP && rate.from === SystemCurrency.USDT
      );
      state.entity.exchangeRate = exchangeRate?.rate ?? state.entity.exchangeRate;
    });
};
